import type { PropertyData } from '@strata-monorepo/shared-types'
import { mapSquareFootageToSize } from './mapSquareFootageToSize'
import { isFieldUsable } from '@strata-monorepo/utils/homeInfoFields'

export interface PrefilledHomeInfo {
  homeSize?: string
  numBedrooms?: string
  numBathrooms?: string
  numFloors?: string
  yearBuilt?: string
}

const YEAR_RANGES = [
  { threshold: 2020, display: '2020 or newer' },
  { threshold: 2010, display: '2010-2019' },
  { threshold: 2000, display: '2000-2009' },
  { threshold: 1990, display: '1990-1999' },
  { threshold: 1980, display: '1980-1989' },
  { threshold: 1970, display: '1970-1979' },
  { threshold: 1960, display: '1960-1969' },
  { threshold: 1940, display: '1940-1959' },
] as const

/**
 * Converts a year number to a year range string matching the form options
 */
const convertYearToRange = (year: number | null | undefined): string | undefined => {
  if (!year || year < 1940) {
    return undefined
  }
  return YEAR_RANGES.find((range) => year >= range.threshold)?.display
}

/**
 * Converts a number to a string format with a maximum threshold
 * Returns undefined for invalid values, the string representation if <= maxValue, or the suffix if > maxValue
 */
const convertCountToRangeFormat = (
  count: number | null | undefined,
  maxValue: number,
  suffix: string,
): string | undefined => {
  if (!count) {
    return undefined
  }

  const roundedCount = Math.round(count)

  if (roundedCount <= 0) {
    return undefined
  }

  if (roundedCount <= maxValue) {
    return String(roundedCount)
  }

  return suffix
}

/**
 * Converts a number to a string format for bedrooms/bathrooms (1-4 as strings, 5+ as "5+")
 */
const convertCountToBedroomBathroomFormat = (
  count: number | null | undefined,
): string | undefined => {
  return convertCountToRangeFormat(count, 4, '5+')
}

/**
 * Converts a number to a string format for floors (1-3 as strings, 4+ as "4+")
 */
const convertCountToFloorFormat = (count: number | null | undefined): string | undefined => {
  return convertCountToRangeFormat(count, 3, '4+')
}

/**
 * Maps propertyData values to homeInfo form field values for prefilling
 * Only includes fields that have valid values in propertyData
 */
export const prefillHomeInfoFromPropertyData = (
  propertyData: PropertyData | null | undefined,
): PrefilledHomeInfo => {
  if (!propertyData) {
    return {}
  }

  const prefilled: PrefilledHomeInfo = {}

  // Map squareFootage to homeSize — skip if imputed
  if (isFieldUsable(propertyData.squareFootage, propertyData.squareFootageImputed)) {
    const homeSize = mapSquareFootageToSize(propertyData.squareFootage ?? 0)
    if (homeSize) {
      prefilled.homeSize = homeSize
    }
  }

  // Map bedrooms to numBedrooms — skip if imputed
  if (isFieldUsable(propertyData.bedrooms, propertyData.bedroomsImputed)) {
    const numBedrooms = convertCountToBedroomBathroomFormat(propertyData.bedrooms)
    if (numBedrooms) {
      prefilled.numBedrooms = numBedrooms
    }
  }

  // Map bathrooms to numBathrooms — skip if imputed
  if (isFieldUsable(propertyData.bathrooms, propertyData.bathroomsImputed)) {
    const numBathrooms = convertCountToBedroomBathroomFormat(propertyData.bathrooms)
    if (numBathrooms) {
      prefilled.numBathrooms = numBathrooms
    }
  }

  // Map features.floorCount to numFloors — skip if featuresImputed (single flag covers all features)
  if (isFieldUsable(propertyData.features?.floorCount, propertyData.featuresImputed)) {
    const numFloors = convertCountToFloorFormat(propertyData.features?.floorCount)
    if (numFloors) {
      prefilled.numFloors = numFloors
    }
  }

  // Map yearBuilt to yearBuilt range string — skip if imputed
  if (isFieldUsable(propertyData.yearBuilt, propertyData.yearBuiltImputed)) {
    const yearBuilt = convertYearToRange(propertyData.yearBuilt)
    if (yearBuilt) {
      prefilled.yearBuilt = yearBuilt
    }
  }

  return prefilled
}
